import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Trash2, Star } from "lucide-react";

export default function PainterGallery() {
  const [items, setItems] = useState([]);
  const [title, setTitle] = useState("");
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get("/painter/gallery");
      setItems(r.data.items || []);
    } catch (e) {
      // Owner accounts + brand-new registrations have no painter record yet;
      // surface a clean empty state instead of blocking with an error toast.
      if (e?.response?.status === 404) { setItems([]); return; }
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => { load(); }, []);

  const upload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file || !title) { toast.error("Title and image required"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("title", title); fd.append("caption", caption);
      await api.post("/painter/gallery", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setTitle(""); setCaption(""); if (fileRef.current) fileRef.current.value = "";
      await load();
      toast.success("Photo added to gallery");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const toggleFeatured = async (g) => {
    try { await api.patch(`/painter/gallery/${g.id}`, { is_featured: !g.is_featured }); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (g) => {
    if (!window.confirm("Remove this photo from your gallery?")) return;
    try { await api.delete(`/painter/gallery/${g.id}`); await load(); toast.success("Removed"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="painter-gallery" className="space-y-8">
      <div>
        <div className="overline mb-2">Gallery</div>
        <h1 className="font-serif text-3xl text-brand-ink mb-1">Recent work</h1>
        <p className="text-sm text-brand-muted">Upload at least 3 photos before approval. We recommend a clean before/after pair.</p>
      </div>

      <div className="border border-brand-linen p-5 bg-white space-y-4">
        <div className="overline">Add a photo</div>
        <input data-testid="gallery-title" placeholder="Title — e.g. Federation home, Newtown"
          value={title} onChange={(e) => setTitle(e.target.value)}
          className="w-full border border-brand-linen p-3 text-sm" />
        <input data-testid="gallery-caption" placeholder="Short caption (optional)"
          value={caption} onChange={(e) => setCaption(e.target.value)}
          className="w-full border border-brand-linen p-3 text-sm" />
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" data-testid="gallery-file"
          className="text-sm" />
        <button onClick={upload} disabled={busy} data-testid="gallery-upload"
          className="px-5 py-2.5 bg-brand-navy text-white text-sm disabled:opacity-50">
          {busy ? "Uploading…" : "Add to gallery"}
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4" data-testid="gallery-grid">
        {items.length === 0 && <div className="text-sm text-brand-muted">No photos yet.</div>}
        {items.map((g) => (
          <div key={g.id} className="border border-brand-linen bg-white" data-testid={`gallery-item-${g.id}`}>
            <img src={g.image_url} alt={g.title} className="w-full aspect-[4/3] object-cover" />
            <div className="p-3 space-y-1">
              <div className="text-sm text-brand-ink">{g.title}</div>
              {g.caption && <div className="text-xs text-brand-muted">{g.caption}</div>}
              <div className="flex justify-between pt-2">
                <button onClick={() => toggleFeatured(g)} title="Toggle featured"
                  data-testid={`gallery-feature-${g.id}`}
                  className={`p-1.5 ${g.is_featured ? "text-brand-teal" : "text-brand-muted"} hover:text-brand-ink`}>
                  <Star className="w-4 h-4" fill={g.is_featured ? "currentColor" : "none"} />
                </button>
                <button onClick={() => remove(g)} title="Remove"
                  data-testid={`gallery-remove-${g.id}`}
                  className="p-1.5 text-brand-muted hover:text-red-600">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
